import { useSyncExternalStore } from 'react';

import type { Value } from '@spotify-confidence/sdk';

export const FLAG_OVERRIDES_STORAGE_KEY = 'devbar-flag-overrides';

export const EMPTY_OVERRIDES: Record<string, unknown> = Object.freeze(
  {},
) as Record<string, unknown>;

export interface OverrideStore {
  subscribe(listener: () => void): () => void;
  getSnapshot(): Record<string, unknown>;
  getServerSnapshot(): Record<string, unknown>;
}

// Singleton-style store for flag overrides. All consumers share one
// storage listener and one JSON.parse per change instead of N.
// Exposed as a factory for test isolation; `overrideStore` below is the
// instance used in production.
export function createOverrideStore(
  storageKey: string = FLAG_OVERRIDES_STORAGE_KEY,
): OverrideStore {
  const listeners = new Set<() => void>();
  let snapshot: Record<string, unknown> = EMPTY_OVERRIDES;
  let lastRaw: string | null = null;
  let listening = false;

  function sync() {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(storageKey);
    } catch {
      // sessionStorage unavailable
    }
    if (raw === lastRaw) return;
    lastRaw = raw;
    try {
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      snapshot =
        typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
          ? (parsed as Record<string, unknown>)
          : EMPTY_OVERRIDES;
    } catch {
      snapshot = EMPTY_OVERRIDES;
    }
  }

  function onStorageEvent(e: StorageEvent) {
    if (e.key !== storageKey) return;
    sync();
    listeners.forEach(l => l());
  }

  function ensureListening() {
    if (listening) return;
    listening = true;
    sync();
    window.addEventListener('storage', onStorageEvent);
  }

  return {
    subscribe(listener: () => void) {
      ensureListening();
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      ensureListening();
      return snapshot;
    },
    getServerSnapshot() {
      return EMPTY_OVERRIDES;
    },
  };
}

export const overrideStore = createOverrideStore();

export function useOverrides(): Record<string, unknown> {
  return useSyncExternalStore(
    overrideStore.subscribe,
    overrideStore.getSnapshot,
    overrideStore.getServerSnapshot,
  );
}

export function applyOverride<T>(
  flagName: string,
  resolved: T,
  overrides: Record<string, unknown>,
): T {
  if (!(flagName in overrides)) return resolved;
  const override = overrides[flagName];
  if (
    typeof resolved === 'object' &&
    resolved !== null &&
    typeof override === 'object' &&
    override !== null
  ) {
    return { ...resolved, ...override } as T;
  }
  return override as T;
}

// Applies any override for `flag` on top of an already-resolved flag
// value. Takes the resolved value rather than the flag-resolution hook so the
// caller (e.g. `useFeatureFlag`) invokes `useFlag` at its own top level —
// keeping the rules-of-hooks contract obvious to both React and eslint.
export function useLocalOverride<T extends Value>(
  flag: string,
  resolved: T,
): T {
  const overrides = useOverrides();
  if (overrides === EMPTY_OVERRIDES) return resolved;
  return applyOverride(flag, resolved, overrides);
}
